import apiClient from './apiClient';
import { withActingAs } from './actingAs';
import type { ApiResponse } from '../types';
import type {
  ScheduleDetailResponse,
  SchedulePositionsResponse,
  ScheduleSummaryResponse,
  ScheduleViewMode,
} from '../types/schedule';

const API_TYPE = 'api' as const;

function unwrap<T>(response: ApiResponse<T> | T, errorMsg: string): T {
  if ((response as any)?.success !== undefined) {
    const wrapped = response as ApiResponse<T>;
    if (!wrapped.success || !wrapped.data) {
      throw new Error(wrapped.message || errorMsg);
    }
    return wrapped.data;
  }
  return response as T;
}

class ScheduleService {
  /**
   * Lightweight main-list payload: one row per strategy + day-level totals.
   * Polled every 30 s while viewing today.
   *
   * `viewMode` reshapes which positions the backend aggregates:
   *   - default: today's date-scoped behavior (entered=D OR closed=D OR live+today)
   *   - entries: only positions whose execution_date == D
   *   - full:    cross-date positions from in-scope executions (DVR on past days)
   */
  async getScheduleSummary(
    date: string,
    viewMode: ScheduleViewMode = 'default',
  ): Promise<ScheduleSummaryResponse> {
    const qp = new URLSearchParams({ date, format: 'summary', view_mode: viewMode });
    withActingAs(qp);
    try {
      const response = await apiClient.get<ApiResponse<ScheduleSummaryResponse> | ScheduleSummaryResponse>(
        `/execution-schedule?${qp.toString()}`,
        undefined,
        API_TYPE,
      );
      return unwrap(response, 'Failed to fetch execution schedule');
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || error.message || 'Failed to fetch execution schedule',
      );
    }
  }

  /**
   * Per-strategy leg-level detail. Fetched on strategy-modal open only.
   * Implicit auth: the backend filters by user_id partition key, so an unknown
   * basket/strategy pair returns an empty rows[] rather than leaking data.
   *
   * `viewMode` is threaded through so the modal's leg cards reflect the page's
   * mode — without it, leg cards would stay date-scoped even when the page is
   * in Full mode.
   */
  async getStrategyDetail(
    date: string,
    basketId: string,
    strategyId: string,
    viewMode: ScheduleViewMode = 'default',
  ): Promise<ScheduleDetailResponse> {
    const qp = new URLSearchParams({
      date,
      format: 'detail',
      basket_id: basketId,
      strategy_id: strategyId,
      view_mode: viewMode,
    });
    withActingAs(qp);
    try {
      const response = await apiClient.get<ApiResponse<ScheduleDetailResponse> | ScheduleDetailResponse>(
        `/execution-schedule?${qp.toString()}`,
        undefined,
        API_TYPE,
      );
      return unwrap(response, 'Failed to fetch strategy detail');
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || error.message || 'Failed to fetch strategy detail',
      );
    }
  }

  /**
   * Flat positions list across every allocated strategy. Powers the Positions
   * tab in the unified Schedule hub (Phase B3). Row shape is identical to
   * `format=detail`'s rows[] — same `_overlay_position` shape from the
   * backend — but spanning all strategies and filtered to overlay rows
   * (synthesized scheduled-not-yet-fired placeholders are dropped server-side).
   *
   * `viewMode` reshapes which positions surface, mirroring summary/detail:
   *   - default: today's date-scoped + live carry-overs
   *   - entries: only positions whose execution_date == D
   *   - full:    cross-date scope from in-scope executions
   */
  async getPositions(
    date: string,
    viewMode: ScheduleViewMode = 'default',
  ): Promise<SchedulePositionsResponse> {
    const qp = new URLSearchParams({ date, format: 'positions', view_mode: viewMode });
    withActingAs(qp);
    try {
      const response = await apiClient.get<ApiResponse<SchedulePositionsResponse> | SchedulePositionsResponse>(
        `/execution-schedule?${qp.toString()}`,
        undefined,
        API_TYPE,
      );
      return unwrap(response, 'Failed to fetch positions');
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || error.message || 'Failed to fetch positions',
      );
    }
  }
}

const scheduleService = new ScheduleService();
export default scheduleService;
export { ScheduleService };
